import type { Poll, PollVoterEntry } from "@client/types/meet.types";

export function emptyVotersByOption(): Map<number, PollVoterEntry[]> {
  return new Map();
}

export function cloneVotersByOption(source: Map<number, PollVoterEntry[]>): Map<number, PollVoterEntry[]> {
  const next = new Map<number, PollVoterEntry[]>();
  for (const [optionId, entries] of source) {
    next.set(
      optionId,
      entries.map((e) => ({ ...e }))
    );
  }
  return next;
}

export function buildVotersByOptionFromVotes(
  votes: { optionId: number; voterId: string; voterName?: string }[],
  isAnonymous: boolean
): Map<number, PollVoterEntry[]> {
  if (isAnonymous) return emptyVotersByOption();

  const map = new Map<number, PollVoterEntry[]>();
  for (const vote of votes) {
    const voterName = vote.voterName?.trim() || "Guest";
    const list = map.get(vote.optionId) ?? [];
    if (!list.some((entry) => entry.voterId === vote.voterId)) {
      list.push({ voterId: vote.voterId, voterName });
    }
    map.set(vote.optionId, list);
  }
  return map;
}

export function processPollVote(poll: Poll, optionId: number, voterId: string, voterName?: string): Poll {
  const newVotes = new Map(poll.votes);
  const newVoters = new Map(poll.voters);
  const previousOptionId = newVoters.get(voterId);

  if (previousOptionId === optionId) return poll;

  if (previousOptionId !== undefined) {
    const prevCount = newVotes.get(previousOptionId) || 0;
    if (prevCount > 0) {
      newVotes.set(previousOptionId, prevCount - 1);
    }
  }

  newVotes.set(optionId, (newVotes.get(optionId) || 0) + 1);
  newVoters.set(voterId, optionId);

  let votersByOption = poll.votersByOption;
  if (!poll.isAnonymous) {
    votersByOption = cloneVotersByOption(poll.votersByOption);
    for (const [optId, entries] of votersByOption) {
      votersByOption.set(
        optId,
        entries.filter((entry) => entry.voterId !== voterId)
      );
    }
    const displayName = voterName?.trim() || "Guest";
    const targetList = votersByOption.get(optionId) ?? [];
    targetList.push({ voterId, voterName: displayName });
    votersByOption.set(optionId, targetList);
  }

  return { ...poll, votes: newVotes, voters: newVoters, votersByOption };
}
